import type { GuardStatus } from '../api/types';
import { useT } from '../i18n';
import { GUARD_DISTANCE, guardCmd, round1, useGuard } from '../lib/guard';

/** Shield icon (inline stroke SVG, no emoji). */
export const IconShield = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6l-8-3z" />
  </svg>
);

type Tone = 'off' | 'paused' | 'ok' | 'warn' | 'alarm' | 'blind';

const TONE_COLOR: Record<Tone, string> = {
  off: 'var(--muted)',
  paused: 'var(--accent)',
  ok: 'var(--green)',
  warn: 'var(--accent)',
  alarm: 'var(--red)',
  blind: 'var(--muted)',
};

function toneOf(g: GuardStatus): Tone {
  if (!g.enabled) return 'off';
  if (g.paused) return 'paused';
  if (g.level === 'TOUCH' || g.level === 'ALARM') return 'alarm';
  if (g.level === 'WARN') return 'warn';
  if (g.lidar === false) return 'blind';
  return 'ok';
}

const untilStr = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

/** One line: the level (or off / paused / does not see) and, while guarding, the phase. */
function stateText(t: ReturnType<typeof useT>, g: GuardStatus): string {
  switch (toneOf(g)) {
    case 'off':
      return t('guard.off');
    case 'paused':
      return t('guard.state.paused', { until: untilStr(g.pausedUntil) });
    case 'alarm':
      return g.level === 'TOUCH' ? t('guard.state.touch') : t('guard.state.alarm');
    case 'warn':
      return t('guard.state.warn');
    case 'blind':
      return t('guard.state.blind');
    default:
      return g.phase === 'learning' ? `${t('guard.state.watching')} · ${t('guard.state.learning')}` : g.phase === 'moving' ? t('guard.state.moving') : t('guard.state.watching');
  }
}

function Stepper({ value, onDec, onInc, canDec, canInc, disabled, decLabel, incLabel }: { value: string; onDec: () => void; onInc: () => void; canDec: boolean; canInc: boolean; disabled: boolean; decLabel: string; incLabel: string }) {
  return (
    <span className="row" style={{ gap: 4 }}>
      <button type="button" className="btn sm" style={{ width: 36, padding: 0, fontSize: 16 }} aria-label={decLabel} disabled={disabled || !canDec} onClick={onDec}>
        −
      </button>
      <span className="mono" style={{ minWidth: 64, textAlign: 'center', fontSize: 13 }}>
        {value}
      </span>
      <button type="button" className="btn sm" style={{ width: 36, padding: 0, fontSize: 16 }} aria-label={incLabel} disabled={disabled || !canInc} onClick={onInc}>
        +
      </button>
    </span>
  );
}

/**
 * Robot page panel: the guard on/off, its distance, the alarm volume, pause and the live state (PROTOCOL §25). Every
 * change is one `guard` command; the robot applies it at once and journals who changed what. Hidden when the robot's
 * app reports no guard (older APK).
 */
export function GuardPanel({ serial, online }: { serial: string; online: boolean }) {
  const t = useT();
  const { guard: g, pending } = useGuard(serial);
  if (!g) return null;
  const dist = g.warnDistance ?? GUARD_DISTANCE.default;
  const busy = pending || !online;
  const set = (args: Parameters<typeof guardCmd>[1]) => void guardCmd(serial, args);
  const tone = toneOf(g);
  const state = stateText(t, g);
  const ros = g.ros ? Object.values(g.ros).every(Boolean) : null;
  return (
    <section className="panel" style={{ gap: 10 }} aria-label={t('guard.title')}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 className="h2" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <IconShield />
          {t('guard.title')}
        </h2>
        <label className="switch" title={g.enabled ? t('guard.turnOff') : t('guard.turnOn')}>
          <input type="checkbox" checked={g.enabled} disabled={busy} aria-label={t('guard.title')} onChange={(e) => set({ enabled: e.target.checked })} />
          <span />
        </label>
      </div>
      <div className="row" style={{ gap: 8, fontSize: 13 }}>
        <span className={`dot${tone === 'ok' ? ' green' : tone === 'alarm' ? ' red' : tone === 'warn' || tone === 'paused' ? ' accent' : ''}`} />
        <span style={{ color: TONE_COLOR[tone], fontWeight: tone === 'alarm' ? 600 : 400 }}>{state}</span>
        {g.enabled && (
          <span className="muted" style={{ fontSize: 12 }}>
            · {g.lidar === false ? t('guard.lidarNo') : t('guard.lidarOk')}
            {ros !== null && ` · ${ros ? t('guard.ros') : t('guard.rosDown')}`}
          </span>
        )}
      </div>
      <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{t('guard.distance')}</span>
        <Stepper
          value={`${dist.toFixed(1)} m`}
          canDec={dist > GUARD_DISTANCE.min + 1e-9}
          canInc={dist < GUARD_DISTANCE.max - 1e-9}
          disabled={busy}
          decLabel={t('guard.closer')}
          incLabel={t('guard.farther')}
          onDec={() => set({ warnDistance: round1(Math.max(GUARD_DISTANCE.min, dist - GUARD_DISTANCE.step)) })}
          onInc={() => set({ warnDistance: round1(Math.min(GUARD_DISTANCE.max, dist + GUARD_DISTANCE.step)) })}
        />
      </div>
      <span className="muted" style={{ fontSize: 11, marginTop: -4 }}>
        {t('guard.distanceHint')}
      </span>
      <div className="row wrap" style={{ gap: 8 }}>
        <button type="button" className="btn sm" disabled={busy || !g.enabled} onClick={() => set({ pause: !g.paused })}>
          {g.paused ? t('guard.resume') : t('guard.pause')}
        </button>
        <button type="button" className="btn sm" disabled={busy || Math.abs(dist - GUARD_DISTANCE.default) < 1e-9} onClick={() => set({ warnDistance: GUARD_DISTANCE.default })}>
          {t('guard.reset')}
        </button>
      </div>
      <span className="muted" style={{ fontSize: 11 }}>
        {t('guard.hint')}
      </span>
    </section>
  );
}

/** Fleet tile: the guard's state in one line with its on/off switch (hidden when the app reports no guard). */
export function GuardTileSwitch({ serial }: { serial: string }) {
  const t = useT();
  const { guard: g, pending } = useGuard(serial);
  if (!g) return null;
  const state = stateText(t, g);
  const tone = toneOf(g);
  const dist = g.warnDistance ?? GUARD_DISTANCE.default;
  return (
    <div className="row" style={{ gap: 6, fontSize: 12, marginTop: 2 }} onClick={(e) => e.stopPropagation()}>
      <span style={{ color: TONE_COLOR[tone], display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
        <IconShield size={14} />
        <span className="ellipsis">{g.enabled ? `${t('guard.short')} · ${dist.toFixed(1)} m · ${state}` : state}</span>
      </span>
      <span className="grow" />
      <label className="switch" title={g.enabled ? t('guard.turnOff') : t('guard.turnOn')}>
        <input type="checkbox" checked={g.enabled} disabled={pending} aria-label={t('guard.title')} onChange={(e) => void guardCmd(serial, { enabled: e.target.checked })} />
        <span />
      </label>
    </div>
  );
}
